import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Table,
  Tag,
  Select,
  Row,
  Col,
  Statistic,
  Button,
  message,
  Spin,
  DatePicker,
} from 'antd';
import {
  ReloadOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  getConsumptionRecords,
  getConsumptionStatistics,
  getConsumptionByType,
} from '../../services/wallet';
import './ConsumptionPage.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * 消费记录页面
 * 职责：展示用户消费记录列表、消费统计、按业务类型统计
 * 数据：从业务服务获取消费记录和统计数据
 * 交互：筛选记录、查看统计、刷新数据
 */
const ConsumptionPage = () => {
  const { isMobile } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [typeStats, setTypeStats] = useState([]);

  // 计算默认日期范围（当前日期-1个月 ~ 当前日期）
  const getDefaultDateRange = () => {
    return [dayjs().subtract(1, 'month'), dayjs()];
  };

  // 初始化筛选器，带默认日期范围
  const [filter, setFilter] = useState({
    businessType: null,
    startTime: getDefaultDateRange()[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    endTime: getDefaultDateRange()[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 业务类型映射（与后端 BusinessTypeEnum 保持一致）
  const businessTypeMap = {
    DEBT_PUBLISH: '发布债权',
    DEBT_BATCH: '批量发布',
    NOTICE_SMS: '短信催告',
    NOTICE_EMAIL: '邮件催告',
    NOTICE_PDF: '生成催告函',
    VIEW_CONTACT: '查看联系方式',
    null: '未分类',
    '': '未分类',
  };

  // 加载消费记录
  const loadRecords = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        ...filter,
      };
      const res = await getConsumptionRecords(params);
      if (res?.data) {
        setRecords(res.data.records || []);
        setPagination({
          current: page,
          pageSize,
          total: res.data.total || 0,
        });
      }
    } catch (error) {
      message.error(error.message || '加载消费记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStatistics = async () => {
    try {
      const [statsRes, typeRes] = await Promise.all([
        getConsumptionStatistics(),
        getConsumptionByType(),
      ]);

      if (statsRes?.data) setStatistics(statsRes.data);
      if (typeRes?.data) setTypeStats(typeRes.data || []);
    } catch (error) {
      console.error('加载统计数据失败', error);
    }
  };

  useEffect(() => {
    loadRecords(1, 10);
    loadStatistics();
  }, []);

  // 刷新数据
  const handleRefresh = () => {
    loadRecords(pagination.current, pagination.pageSize);
    loadStatistics();
  };

  // 业务类型筛选变化
  const handleBusinessTypeChange = (value) => {
    setFilter({ ...filter, businessType: value || null });
  };

  // 日期范围变化
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      // 检查日期范围是否超过1个月（31天）
      const daysDiff = dates[1].diff(dates[0], 'day');
      if (daysDiff > 31) {
        message.warning('时间范围不能超过1个月，请重新选择');
        return;
      }
      setFilter({
        ...filter,
        startTime: dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endTime: dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      });
    } else {
      setFilter({ ...filter, startTime: null, endTime: null });
    }
  };

  // 限制日期选择范围为过去2年到当前日期
  const disabledDate = (current) => {
    const now = dayjs();
    const minDate = now.subtract(2, 'year');
    // 只允许选择过去2年到今天的日期
    return current && (current < minDate.startOf('day') || current > now.endOf('day'));
  };

  // 应用筛选
  const handleApplyFilter = () => {
    loadRecords(1, pagination.pageSize);
  };

  // 重置筛选
  const handleResetFilter = () => {
    setFilter({
      businessType: null,
      startTime: null,
      endTime: null,
    });
    loadRecords(1, pagination.pageSize);
  };

  // 消费状态渲染
  const renderStatus = (status) => {
    const statusMap = {
      SUCCESS: { icon: <CheckCircleOutlined />, text: '成功', color: 'success' },
      FAILED: { icon: <CloseCircleOutlined />, text: '失败', color: 'error' },
      REFUNDED: { icon: <RollbackOutlined />, text: '已退还', color: 'default' },
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // 业务类型渲染
  const renderBusinessType = (type) => {
    return businessTypeMap[type] || type || '未分类';
  };

  // 金蝉值变化渲染
  const renderPoints = (points, status) => {
    const isRefund = status === 'REFUNDED';
    const className = isRefund ? 'points-refund' : 'points-deduct';
    const sign = isRefund ? '+' : '-';
    return <span className={className}>{sign}{Math.abs(points)}</span>;
  };

  // 表格列
  const columns = [
    {
      title: '业务类型',
      dataIndex: 'businessType',
      key: 'businessType',
      width: 120,
      render: renderBusinessType,
    },
    {
      title: '业务描述',
      dataIndex: 'businessDesc',
      key: 'businessDesc',
      ellipsis: true,
    },
    {
      title: '消费金蝉值',
      dataIndex: 'points',
      key: 'points',
      width: 100,
      render: (points, record) => renderPoints(points, record.status),
    },
    {
      title: '消费前余额',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      width: 110,
    },
    {
      title: '消费后余额',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 110,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: renderStatus,
    },
    {
      title: '消费时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 150,
      render: (time) => new Date(time).toLocaleString('zh-CN'),
    },
  ];

  // 按业务类型统计表格列
  const typeStatsColumns = [
    {
      title: '业务类型',
      dataIndex: 'businessType',
      key: 'businessType',
      render: renderBusinessType,
    },
    {
      title: '消费次数',
      dataIndex: 'count',
      key: 'count',
      width: 100,
    },
    {
      title: '累计金蝉值',
      dataIndex: 'totalPoints',
      key: 'totalPoints',
      width: 100,
      render: (points) => <span className="points-deduct">{points}</span>,
    },
  ];

  return (
    <div className="page-container p-consumption-page">
      <div className="page-content">
        <div className="page-title-with-action">
          <div className="page-title">消费记录</div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            刷新
          </Button>
        </div>

        {/* 统计卡片 */}
        {statistics && (
          <Row gutter={[16, 16]} className="stats-row">
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="总消费次数"
                  value={statistics.totalCount || 0}
                  suffix="次"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="累计消费金蝉值"
                  value={statistics.totalPoints || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="业务类型数"
                  value={statistics.typeCount || 0}
                  suffix="种"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="平均每次消费"
                  value={statistics.totalCount
                    ? Math.round(statistics.totalPoints / statistics.totalCount)
                    : 0
                  }
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 按类型统计 */}
        {typeStats.length > 0 && (
          <Card title="按业务类型统计" bordered={false} className="type-stats-card">
            <Table
              columns={typeStatsColumns}
              dataSource={typeStats}
              rowKey="businessType"
              pagination={false}
              size="small"
            />
          </Card>
        )}

        {/* 筛选区域 */}
        <Card bordered={false} className="filter-card">
          <div className="filter-section">
            <div className="filter-item">
              <label>业务类型：</label>
              <Select
                placeholder="全部类型"
                allowClear
                style={{ width: 150 }}
                value={filter.businessType}
                onChange={handleBusinessTypeChange}
              >
                {Object.entries(businessTypeMap).map(([key, value]) => (
                  <Option key={key} value={key}>{value}</Option>
                ))}
              </Select>
            </div>
            <div className="filter-item">
              <label>时间范围：</label>
              <RangePicker
                placeholder={['开始时间', '结束时间']}
                value={filter.startTime && filter.endTime ? [dayjs(filter.startTime), dayjs(filter.endTime)] : getDefaultDateRange()}
                disabledDate={disabledDate}
                onChange={handleDateRangeChange}
              />
            </div>
            <div className="filter-actions">
              <Button type="primary" onClick={handleApplyFilter}>
                查询
              </Button>
              <Button onClick={handleResetFilter}>
                重置
              </Button>
            </div>
          </div>
        </Card>

        {/* 消费记录表格 */}
        <Card bordered={false} className="records-card">
          <Table
            columns={columns}
            dataSource={records}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条记录`,
            }}
            onChange={(newPagination) => loadRecords(newPagination.current, newPagination.pageSize)}
            scroll={{ x: 1000 }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Card>
      </div>
    </div>
  );
};

export default ConsumptionPage;
